import { useEffect, useState } from "react";
import api, { formatApiError, ONEGO_LOGO } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Link, useNavigate } from "react-router-dom";
import { LogOut, UserPlus, Shield } from "lucide-react";
import { toast } from "sonner";

function fmt(n) {
    return new Intl.NumberFormat("en-KE", { maximumFractionDigits: 0 }).format(n || 0);
}

export default function SuperAgentConsole() {
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const [overview, setOverview] = useState(null);
    const [email, setEmail] = useState("");
    const [loading, setLoading] = useState(false);

    const load = async () => {
        try {
            const { data } = await api.get("/agent/overview");
            setOverview(data);
        } catch (_) {}
    };

    useEffect(() => { load(); }, []);

    const recruit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            await api.post("/agent/recruit", { merchant_email: email });
            toast.success("Merchant assigned to your network");
            setEmail("");
            await load();
        } catch (err) {
            toast.error(formatApiError(err?.response?.data?.detail) || err.message);
        } finally {
            setLoading(false);
        }
    };

    if (user && !["super_agent", "admin"].includes(user.role)) {
        return (
            <div className="min-h-screen flex items-center justify-center p-6 text-center" data-testid="agent-forbidden">
                <div className="og-card p-10 max-w-md">
                    <Shield size={32} className="mx-auto text-rose-500" />
                    <h1 className="mt-4 font-display text-2xl font-bold text-onego-navy">Super-agents only</h1>
                    <p className="mt-2 text-slate-600">Sign in with a super-agent account to access this console.</p>
                    <Link to="/login" className="og-btn-gradient inline-flex mt-5">Sign in</Link>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-onego-bg" data-testid="agent-console">
            <header className="sticky top-0 z-30 bg-white border-b border-slate-200">
                <div className="px-6 lg:px-10 py-4 flex items-center justify-between">
                    <Link to="/" className="flex items-center gap-3">
                        <img src={ONEGO_LOGO} alt="OneGo" className="h-8" />
                        <span className="og-chip">Super-Agent</span>
                    </Link>
                    <button onClick={async () => { await logout(); navigate("/"); }} className="og-btn-ghost inline-flex items-center gap-2" data-testid="agent-logout">
                        <LogOut size={16} /> Sign out
                    </button>
                </div>
            </header>

            <main className="max-w-6xl mx-auto px-6 lg:px-10 py-8 space-y-8">
                <h1 className="font-display text-4xl font-extrabold text-onego-navy">Your agent network</h1>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="og-card p-6">
                        <p className="text-xs uppercase tracking-widest font-semibold text-slate-500">Merchants recruited</p>
                        <p className="mt-3 font-display text-3xl font-extrabold text-onego-navy" data-testid="agent-recruited">{fmt(overview?.merchants_recruited)}</p>
                    </div>
                    <div className="og-card p-6">
                        <p className="text-xs uppercase tracking-widest font-semibold text-slate-500">Network GMV</p>
                        <p className="mt-3 font-display text-3xl font-extrabold text-onego-navy">KES {fmt(overview?.gmv)}</p>
                    </div>
                    <div className="og-card p-6">
                        <p className="text-xs uppercase tracking-widest font-semibold text-slate-500">Your override</p>
                        <p className="mt-3 font-display text-3xl font-extrabold og-gradient-text">KES {fmt(overview?.commission_pool)}</p>
                    </div>
                </div>

                <form onSubmit={recruit} className="og-card p-7" data-testid="agent-recruit-form">
                    <h3 className="font-display text-xl font-bold text-onego-navy flex items-center gap-2"><UserPlus size={18} /> Recruit a merchant</h3>
                    <p className="text-sm text-slate-500">Assign an existing merchant to your network and start earning override commission.</p>
                    <div className="mt-4 flex flex-col md:flex-row gap-3">
                        <input className="og-input flex-1" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="merchant@onego.africa" data-testid="agent-recruit-email" />
                        <button type="submit" disabled={loading} className="og-btn-gradient disabled:opacity-60" data-testid="agent-recruit-submit">
                            {loading ? "Assigning\u2026" : "Add to my network"}
                        </button>
                    </div>
                </form>

                <div className="og-card p-7">
                    <h3 className="font-display text-xl font-bold text-onego-navy">Your merchants</h3>
                    <div className="mt-3 divide-y divide-slate-100">
                        {(!overview?.merchants || overview.merchants.length === 0) && (
                            <p className="py-6 text-sm text-slate-500">No merchants yet. Recruit your first one above.</p>
                        )}
                        {overview?.merchants?.map((m) => (
                            <div key={m.id} className="py-3 flex items-center justify-between" data-testid={`agent-merchant-${m.id}`}>
                                <div>
                                    <p className="font-bold text-onego-navy">{m.business_name || m.name}</p>
                                    <p className="text-xs text-slate-500">{m.email} &middot; {m.country}</p>
                                </div>
                                <span className="og-chip">merchant</span>
                            </div>
                        ))}
                    </div>
                </div>
            </main>
        </div>
    );
}
